import { useCallback, useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { ApiError } from '@formosa/shared/api/createClient';

const EMPTY_FORM = {
  durationSeconds: '',
  payoutPercent: '',
};

function payoutOddsToPercent(payoutOdds) {
  const num = Number(payoutOdds);
  if (Number.isNaN(num)) {
    return '';
  }
  return String(Math.round(num * 100));
}

function percentToPayoutOdds(percent) {
  const num = Number(percent);
  if (Number.isNaN(num)) {
    return 0;
  }
  return num / 100;
}

export default function TimedDurationModal({ open, product, onClose }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [filterId, setFilterId] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const loadRecords = useCallback(async () => {
    if (!product?.id) {
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      const list = await adminApi.listTimedDurations(product.id);
      setRecords(Array.isArray(list) ? list : []);
    } catch (err) {
      setMessage(err instanceof ApiError ? err.message : '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [product?.id]);

  useEffect(() => {
    if (open && product?.id) {
      loadRecords();
      setFilterId('');
      setFormOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
    }
  }, [open, product?.id, loadRecords]);

  if (!open || !product) {
    return null;
  }

  const filtered = records.filter((row) => {
    if (!filterId.trim()) {
      return true;
    }
    return String(row.id).includes(filterId.trim());
  });

  function openCreateForm() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
    setMessage('');
  }

  function openEditForm(row) {
    setEditing(row);
    setForm({
      durationSeconds: String(row.durationSeconds ?? ''),
      payoutPercent: payoutOddsToPercent(row.payoutOdds),
    });
    setFormOpen(true);
    setMessage('');
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const durationSeconds = Number(form.durationSeconds);
    const payoutOdds = percentToPayoutOdds(form.payoutPercent);
    if (!durationSeconds || durationSeconds <= 0) {
      setMessage('請輸入結算時間');
      return;
    }
    if (!payoutOdds || payoutOdds <= 0) {
      setMessage('請輸入收益率');
      return;
    }
    setSubmitting(true);
    setMessage('');
    try {
      if (editing) {
        await adminApi.updateTimedDuration({
          id: editing.id,
          durationSeconds,
          payoutOdds,
        });
        setMessage('修改成功');
      } else {
        await adminApi.createTimedDuration({
          parentId: product.id,
          durationSeconds,
          payoutOdds,
        });
        setMessage('新增成功');
      }
      setFormOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      await loadRecords();
    } catch (err) {
      setMessage(err instanceof ApiError ? err.message : '操作失敗');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(row) {
    if (!window.confirm(`確定刪除產品 ID ${row.id}？`)) {
      return;
    }
    setMessage('');
    try {
      await adminApi.deleteTimedDuration(row.id);
      setMessage('刪除成功');
      await loadRecords();
    } catch (err) {
      setMessage(err instanceof ApiError ? err.message : '刪除失敗');
    }
  }

  return (
    <div className="admin-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="admin-modal admin-modal--wide"
        role="dialog"
        aria-labelledby="timed-duration-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal__header">
          <h3 id="timed-duration-title">限時交易</h3>
          <button type="button" className="admin-modal__close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="admin-modal__body">
          <div className="timed-duration-toolbar">
            <label>
              產品ID：
              <input
                type="text"
                value={filterId}
                onChange={(e) => setFilterId(e.target.value)}
                placeholder="請輸入產品ID"
              />
            </label>
            <button type="button" className="btn btn-sm" onClick={() => setFilterId('')}>
              重置
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={openCreateForm}>
              新增
            </button>
          </div>

          <p className="form-hint">
            交易對：{product.name || product.marketSymbolCode}
          </p>

          {message && <p className="form-hint form-hint--danger">{message}</p>}

          <table className="admin-table">
            <thead>
              <tr>
                <th>產品ID</th>
                <th>結算時間(秒)(限時)</th>
                <th>收益率(%)(限時)</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="empty-cell">載入中…</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className="empty-cell">尚無子產品</td>
                </tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>{row.durationSeconds}</td>
                    <td>{payoutOddsToPercent(row.payoutOdds)}%</td>
                    <td className="action-cell">
                      <button type="button" className="btn btn-success btn-sm" onClick={() => openEditForm(row)}>
                        修改
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row)}>
                        刪除
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {formOpen && (
            <form className="timed-duration-form" onSubmit={handleSubmit}>
              <h4>{editing ? '修改' : '新增'}</h4>
              <div className="admin-form-row">
                <label className="admin-form-row__label required">產品名稱：</label>
                <div className="admin-form-row__control">
                  <input type="text" value={product.name || product.marketSymbolCode} disabled />
                </div>
              </div>
              <div className="admin-form-row">
                <label className="admin-form-row__label required">結算時間(秒)(限時)：</label>
                <div className="admin-form-row__control admin-form-row__control--suffix">
                  <input
                    type="number"
                    min="1"
                    value={form.durationSeconds}
                    onChange={(e) => setForm((prev) => ({ ...prev, durationSeconds: e.target.value }))}
                    required
                  />
                  <span className="admin-form-row__suffix">秒</span>
                </div>
              </div>
              <div className="admin-form-row">
                <label className="admin-form-row__label required">收益率(%)(限時)：</label>
                <div className="admin-form-row__control admin-form-row__control--suffix">
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={form.payoutPercent}
                    onChange={(e) => setForm((prev) => ({ ...prev, payoutPercent: e.target.value }))}
                    required
                  />
                  <span className="admin-form-row__suffix">%</span>
                </div>
              </div>
              <div className="admin-modal__footer admin-modal__footer--center">
                <button type="submit" className="btn btn-success" disabled={submitting}>
                  {submitting ? '提交中…' : '提交'}
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={() => {
                    setFormOpen(false);
                    setEditing(null);
                    setForm(EMPTY_FORM);
                  }}
                >
                  重置
                </button>
                <button type="button" className="btn btn-danger" onClick={() => setFormOpen(false)}>
                  關閉
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="admin-modal__footer admin-modal__footer--center">
          <button type="button" className="btn btn-danger" onClick={onClose}>
            關閉
          </button>
        </div>
      </div>
    </div>
  );
}
